<template>
  <div class="ad-page">
    <!-- Planes -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">Planes</h2>
        <span class="ad-toolbar__spacer"></span>
        <button v-if="archivedCount" type="button" class="ad-btn ad-btn--ghost" @click="showArchived = !showArchived">
          {{ showArchived ? 'Ocultar archivados' : `Ver archivados (${archivedCount})` }}
        </button>
        <button type="button" class="ad-btn ad-btn--primary" @click="planPanel = { plan: null }">＋ Crear plan</button>
      </div>
      <p v-if="plansError" class="ad-error">{{ plansError }}</p>
      <p v-else-if="!visiblePlans.length && !plansLoading" class="ad-card ad-empty">Aún no hay planes. Crea el primero con "＋ Crear plan".</p>
      <div class="plans">
        <article v-for="p in visiblePlans" :key="p.id" class="ad-card plan" :class="{ 'plan--archived': p.archived }">
          <header class="plan__head">
            <h3 class="plan__name">{{ p.name }}</h3>
            <span v-if="p.archived" class="ad-pill ad-pill--gray">Archivado</span>
          </header>
          <div class="plan__price"><span class="ad-mono plan__amount">{{ money(p.monthlyPrice) }}</span><span class="plan__per"> / mes</span></div>
          <ul v-if="p.features.length" class="plan__features">
            <li v-for="f in p.features" :key="f"><span class="mdi mdi-check"></span>{{ f }}</li>
          </ul>
          <p class="plan__limits">
            {{ p.maxUsers ? `Hasta ${p.maxUsers} usuarios` : 'Usuarios sin límite' }} ·
            {{ p.maxPlots ? `hasta ${p.maxPlots} naves` : 'naves sin límite' }}
          </p>
          <footer class="plan__foot">
            <span class="ad-hint">{{ p.parksCount }} {{ p.parksCount === 1 ? 'parque' : 'parques' }}</span>
            <span class="ad-toolbar__spacer"></span>
            <button type="button" class="ad-btn ad-btn--ghost small" @click="planPanel = { plan: p }">Editar</button>
            <button type="button" class="ad-btn ad-btn--ghost small" @click="toggleArchive(p)">{{ p.archived ? 'Restaurar' : 'Archivar' }}</button>
          </footer>
        </article>
      </div>
    </section>

    <!-- Cobros por parque -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">Cobros por parque</h2>
        <span class="ad-toolbar__spacer"></span>
        <div class="ad-search">
          <span class="mdi mdi-magnify"></span>
          <input v-model="search" class="ad-input" placeholder="Buscar parque…" aria-label="Buscar parque" />
        </div>
      </div>
      <div class="ad-card">
        <p v-if="loading && !rows.length" class="ad-empty">Cargando…</p>
        <p v-else-if="loadError" class="ad-empty">{{ loadError }}</p>
        <p v-else-if="!rows.length" class="ad-empty">{{ search ? 'Ningún parque coincide.' : 'Aún no hay parques.' }}</p>
        <div v-else class="ad-table-wrap">
          <table class="ad-table">
            <thead>
              <tr><th>Parque</th><th>Plan</th><th>Monto mensual</th><th>Último cobro</th><th>Próximo cobro</th><th>Estatus de pago</th><th class="ad-col-actions"></th></tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id" :class="rowClass(r)">
                <td class="ad-cell-name">{{ r.name }}</td>
                <td>
                  <span class="ad-pill ad-pill--gray">{{ r.plan || 'Sin plan' }}</span>
                  <div v-if="r.requestedPlan" class="ad-cell-sub requested">Pidió: {{ r.requestedPlan }}</div>
                </td>
                <td>
                  <template v-if="r.monthlyAmount !== null">
                    <span class="ad-mono amount">{{ money(r.effectiveAmount) }}</span>
                    <div v-if="r.discountPercent" class="ad-cell-sub">
                      <s>{{ money(r.monthlyAmount) }}</s> · −{{ r.discountPercent }}%<template v-if="r.discountUntil"> hasta {{ formatDate(r.discountUntil) }}</template>
                    </div>
                  </template>
                  <template v-else>—</template>
                </td>
                <td>{{ formatDate(r.lastPaymentAt) }}</td>
                <td>{{ formatDate(r.nextBillingAt) }}</td>
                <td><span class="ad-pill" :class="PAYMENT_STATUS[r.paymentStatus].cls">{{ PAYMENT_STATUS[r.paymentStatus].label }}</span></td>
                <td class="ad-col-actions"><RowMenu :items="menuFor(r)" @select="(key) => onAction(key, r)" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Resumen financiero -->
    <section class="summary">
      <div class="ad-card metric">
        <span class="metric__label">MRR</span>
        <span class="ad-mono metric__value" style="color: #10b981">{{ summary ? money(summary.mrr) : '—' }}</span>
        <span class="metric__sub">Parques activos con plan</span>
      </div>
      <div class="ad-card metric">
        <span class="metric__label">Ingresos del año</span>
        <span class="ad-mono metric__value" style="color: #1e3a5f">{{ summary ? money(summary.yearIncome) : '—' }}</span>
        <span class="metric__sub">Pagos registrados en {{ new Date().getFullYear() }}</span>
      </div>
      <div class="ad-card metric">
        <span class="metric__label">Churn del mes</span>
        <span class="metric__value metric__value--display" :style="{ color: churnColor }">{{ summary ? `${summary.churn.percent}%` : '—' }}</span>
        <span class="metric__sub">
          <template v-if="summary">{{ summary.churn.churned }} de {{ summary.churn.base }} parques suspendidos o eliminados este mes</template>
        </span>
      </div>
    </section>

    <PlanFormPanel v-if="planPanel" :plan="planPanel.plan" @close="planPanel = null" @done="onPlanSaved" />
    <RegisterPaymentModal v-if="modal?.type === 'payment'" :row="modal.row" @close="modal = null" @done="afterAction('Pago registrado')" />
    <ChangePlanModal v-if="modal?.type === 'plan'" :row="modal.row" :plans="activePlans" @close="modal = null" @done="afterAction('Plan actualizado')" />
    <DiscountModal v-if="modal?.type === 'discount'" :row="modal.row" @close="modal = null" @done="afterAction('Descuento aplicado')" />
    <PaymentHistoryModal v-if="modal?.type === 'history'" :row="modal.row" @close="modal = null" />
    <ReasonModal
      v-if="modal?.type === 'overdue'"
      :title="`Marcar como vencido · ${modal.row.name}`"
      message="El parque quedará como Vencido hasta que registres un pago. No pierde el acceso (para eso está Suspender)."
      confirm-label="Marcar como vencido"
      :action="(reason) => markParkOverdue(modal.row.id, reason)"
      @close="modal = null"
      @done="afterAction('Parque marcado como vencido')"
    />
    <div v-if="toast" class="ad-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  listPlans, updatePlan, listBillingParks, getBillingSummary, removeDiscount, markParkOverdue,
} from '@/services/superadmin'
import RowMenu from '@/components/Superadmin/RowMenu.vue'
import PlanFormPanel from '@/components/Superadmin/PlanFormPanel.vue'
import RegisterPaymentModal from '@/components/Superadmin/RegisterPaymentModal.vue'
import ChangePlanModal from '@/components/Superadmin/ChangePlanModal.vue'
import DiscountModal from '@/components/Superadmin/DiscountModal.vue'
import PaymentHistoryModal from '@/components/Superadmin/PaymentHistoryModal.vue'
import ReasonModal from '@/components/Superadmin/ReasonModal.vue'
import { PAYMENT_STATUS, formatDate, money, errorText } from '@/components/Superadmin/adminFormat'

const route = useRoute()

const plans = ref([])
const plansLoading = ref(true)
const plansError = ref('')
const showArchived = ref(false)
const rows = ref([])
const loading = ref(true)
const loadError = ref('')
const search = ref(typeof route.query.search === 'string' ? route.query.search : '')
const summary = ref(null)
const planPanel = ref(null)
const modal = ref(null)
const toast = ref('')

const activePlans = computed(() => plans.value.filter((p) => !p.archived))
const archivedCount = computed(() => plans.value.filter((p) => p.archived).length)
const visiblePlans = computed(() => (showArchived.value ? plans.value : activePlans.value))
// Churn bajo = verde; alto = rojo.
const churnColor = computed(() => (summary.value && summary.value.churn.percent > 3 ? '#e84545' : '#10b981'))

async function loadPlans() {
  plansError.value = ''
  try {
    plans.value = await listPlans()
  } catch (err) {
    plansError.value = errorText(err, 'No se pudieron cargar los planes.')
  } finally {
    plansLoading.value = false
  }
}

let seq = 0
async function loadRows() {
  const mine = ++seq
  loading.value = true
  loadError.value = ''
  try {
    const data = await listBillingParks(search.value.trim())
    if (mine === seq) rows.value = data
  } catch (err) {
    if (mine === seq) loadError.value = errorText(err, 'No se pudieron cargar los cobros.')
  } finally {
    if (mine === seq) loading.value = false
  }
}

async function loadSummary() {
  try {
    summary.value = await getBillingSummary()
  } catch { /* el resumen es secundario: la tabla funciona sin él */ }
}

function reloadAll() {
  loadPlans()
  loadRows()
  loadSummary()
}

let timer = null
watch(search, () => { clearTimeout(timer); timer = setTimeout(loadRows, 250) })
onMounted(reloadAll)

// Filas por vencer en 7 días: fondo ámbar suave. Vencidas: rojo suave.
function rowClass(r) {
  if (r.paymentStatus === 'OVERDUE') return 'row--overdue'
  if (r.paymentStatus === 'DUE_SOON') return 'row--soon'
  return ''
}

function menuFor(r) {
  const hasPlan = !!r.plan
  return [
    { key: 'payment', label: 'Registrar pago manual', icon: 'mdi-cash-plus', disabled: !hasPlan, hint: hasPlan ? '' : 'Primero asigna un plan' },
    { key: 'overdue', label: 'Marcar como vencido', icon: 'mdi-alert-outline', disabled: !['ACTIVE', 'TRIAL'].includes(r.parkStatus) || !hasPlan },
    { key: 'plan', label: hasPlan ? 'Cambiar plan' : 'Asignar plan', icon: 'mdi-swap-horizontal' },
    r.discountPercent
      ? { key: 'discount-off', label: 'Quitar descuento', icon: 'mdi-tag-off-outline' }
      : { key: 'discount', label: 'Aplicar descuento', icon: 'mdi-tag-outline', disabled: !hasPlan, hint: hasPlan ? '' : 'Primero asigna un plan' },
    { divider: true },
    { key: 'history', label: 'Ver historial de pagos', icon: 'mdi-history' },
  ]
}

function flash(text) {
  toast.value = text
  setTimeout(() => { toast.value = '' }, 2600)
}

function afterAction(text) {
  modal.value = null
  flash(text)
  reloadAll()
}

async function onAction(key, row) {
  if (key === 'payment') modal.value = { type: 'payment', row }
  else if (key === 'overdue') modal.value = { type: 'overdue', row }
  else if (key === 'plan') modal.value = { type: 'plan', row }
  else if (key === 'discount') modal.value = { type: 'discount', row }
  else if (key === 'history') modal.value = { type: 'history', row }
  else if (key === 'discount-off') {
    try {
      await removeDiscount(row.id)
      afterAction('Descuento quitado')
    } catch (err) {
      flash(errorText(err))
    }
  }
}

function onPlanSaved() {
  const wasEdit = !!planPanel.value?.plan
  planPanel.value = null
  flash(wasEdit ? 'Plan actualizado' : 'Plan creado')
  loadPlans()
}

async function toggleArchive(plan) {
  try {
    await updatePlan(plan.id, { archived: !plan.archived })
    flash(plan.archived ? 'Plan restaurado' : 'Plan archivado')
    loadPlans()
  } catch (err) {
    flash(errorText(err))
  }
}
</script>

<style scoped>
.requested { color: #b45309; font-weight: 500; }
.sec-head { margin-bottom: 10px; }
.sec-title { margin: 0; font: 700 15px var(--font-display); color: #1e3a5f; }

.plans { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 14px; }
.plan { padding: 16px; display: flex; flex-direction: column; gap: 8px; }
.plan--archived { opacity: 0.65; }
.plan__head { display: flex; align-items: center; gap: 8px; }
.plan__name { margin: 0; flex: 1; font: 700 15px var(--font-display); color: #1e3a5f; }
.plan__amount { font-size: 22px; font-weight: 700; color: #1e3a5f; }
.plan__per { font: 400 12px Inter, sans-serif; color: #64748b; }
.plan__features { list-style: none; margin: 4px 0 0; padding: 0; display: flex; flex-direction: column; gap: 4px; flex: 1; }
.plan__features li { display: flex; gap: 6px; align-items: flex-start; font: 400 12px Inter, sans-serif; color: #475569; }
.plan__features .mdi { color: #10b981; font-size: 14px; margin-top: 1px; }
.plan__limits { margin: 0; font: 400 11px Inter, sans-serif; color: #64748b; }
.plan__foot { display: flex; align-items: center; gap: 6px; padding-top: 8px; border-top: 1px solid #eef1f6; }
.small { padding: 5px 10px; font-size: 12px; }

.amount { color: #1e3a5f; font-size: 12px; }
.ad-table tbody tr.row--soon td { background: #fffbeb; }
.ad-table tbody tr.row--overdue td { background: #fef2f2; }

.summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
.metric { padding: 16px 18px; display: flex; flex-direction: column; gap: 4px; }
.metric__label { font: 600 11px Inter, sans-serif; letter-spacing: 0.04em; text-transform: uppercase; color: #64748b; }
.metric__value { font-size: 24px; font-weight: 700; line-height: 1.2; }
.metric__value--display { font-family: var(--font-display); }
.metric__sub { font: 400 11px Inter, sans-serif; color: #94a3b8; }
</style>